import React from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import { useFormikContext } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Card,
  CardBody,
  CardFooter,
} from 'reactstrap';
import { Button, Field } from '@cordata/buffet';

const ReactGridLayout = WidthProvider(GridLayout);

const LayoutEditor = () => {
  const { values, setFieldValue } = useFormikContext();

  const createFilterGridItem = (filter, index) => (
    <div
      style={{
        border: '1px dashed gray',
        cursor: 'move',
        fontSize: '15px',
      }}
      key={`filter-${index}`}
      data-grid={{
        // these act as default values when the user adds a new filter
        x: 0,
        y: index,
        w: 12,
        h: 1,
        maxH: 1,
      }}
    >
      {filter.type ? (<>
        {filter.type.value === 'date range' ? (<>
          <div className="h-100 w-50 float-left" style={{ padding: '4.5px 9px 1.5px', borderRight: '1px dashed gray' }}>
            <span style={{ color: '#287fb8' }}>{filter.startDateLabel || '<unlabeled filter - start date>'}</span><br />
            <span style={{ color: '#999999' }}>01/01/2000</span>
          </div>

          <div className="h-100 w-50 float-right" style={{ padding: '4.5px 9px 1.5px' }}>
            <span style={{ color: '#287fb8' }}>{filter.endDateLabel || '<unlabeled filter - end date>'}</span><br />
            <span style={{ color: '#999999' }}>12/01/2000</span>
          </div>
        </>) : (<>
          <div style={{ padding: '4.5px 9px 1.5px' }}>
            <span style={{ color: '#287fb8' }}>{filter.label || '<unlabeled filter>'}</span><br />

            {filter.type.value === 'checkbox' && (
              <input disabled type="checkbox" />
            )}

            {filter.type.value === 'badge' && (<>
              <span style={{ color: '#999999' }}>Select</span>
            </>)}

            {!['date range', 'checkbox', 'badge'].includes(filter.type.value) && (
              <span style={{ color: '#999999' }}>Select{!filter.isMultiSelect && ' one'}</span>
            )}
          </div>
        </>)}
      </>) : (
        <span style={{ padding: '4.5px 9px 1.5px', color: '#287fb8' }}>{filter.label || '<unlabeled filter>'}</span>
      )}
    </div>
  );

  const createChartGridItem = (chart, index) => {
    const title = chart.title || `<untitled chart - ${chart.columnName && chart.columnName.value}>`;

    return (
      <div
        style={{ border: '1px dashed gray', cursor: 'move' }}
        className="p-1"
        key={`chart-${index}`}
        data-grid={{
          // these act as default values when the user adds a new chart
          x: 0,
          y: index,
          w: 12,
          h: 6,
          minW: 3,
          minH: 3,
        }}
      >
        <h2 style={{ marginTop: '1.33rem', marginBottom: '1.33rem', fontSize: '1.6rem' }}>{title}</h2>

        <div className="w-100 text-center" style={{ fontSize: '6rem' }}>
          {chart.type && (<>
            {chart.type.value === 'doughnut' ? (
              <FontAwesomeIcon icon="circle-notch" />
            ) : (
              <FontAwesomeIcon icon={`chart-${chart.type.value}`} />
            )}
          </>)}
        </div>
      </div>
    );
  };

  const createTableGridItem = (table, index) => {
    const title = table.title || '<untitled table>';

    return (
      <div
        style={{ border: '1px dashed gray', cursor: 'move', overflow: 'hidden' }}
        className="p-1"
        key={`table-${index}`}
        data-grid={{
          // these act as default values when the user adds a new table
          x: 0,
          y: index + (values.charts || []).length,
          w: 12,
          h: 3,
          minW: 3,
          minH: 3,
        }}
      >
        <div className="d-flex" style={{ marginTop: '1.33rem', marginBottom: '1.33rem' }}>
          <h2 className="d-inline-block" style={{ fontSize: '1.6rem' }}>{title}</h2>

          <div className="ml-auto mr-3">
            <Field
              name={`tables.${index}.fixHeight`}
              type="checkbox"
              label={false}
              placeholder="Fix height"
              title="If checked, table will have scrollbar if it is taller than this box"
            />
          </div>
        </div>

        <table className="w-100" style={{ border: '1px solid #b3b3b3' }}>
          <thead style={{ backgroundColor: '#b3b3b3' }}>
            <tr className="text-white">
              {table.columns && table.columns.map(c => (
                <th style={{ fontWeight: 'normal', padding: '0.5em 1em' }}>
                  {c.displayHeader}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            <tr style={{ color: 'gray', backgroundColor: '#e6e6e6', borderBottom: '1px solid #b3b3b3' }}>
              {table.columns && table.columns.map(c => (
                <td style={{ padding: '0.5em 1em' }}>
                  {c.columnName && c.columnName.value}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const createLagTableGridItem = (lagTable, index) => {
    const title = lagTable.title || '<untitled lag table>';
    const columns = lagTable.columns || [];

    return (
      <div
        style={{ border: '1px dashed #6c5ce7', cursor: 'move', overflow: 'hidden' }}
        className="p-1"
        key={`lagtable-${index}`}
        data-grid={{
          x: 0,
          y: index + (values.charts || []).length + (values.tables || []).length,
          w: 12,
          h: 6,
          minW: 3,
          minH: 3,
        }}
      >
        <div className="d-flex" style={{ marginTop: '1.33rem', marginBottom: '1.33rem' }}>
          <h2 className="d-inline-block" style={{ fontSize: '1.6rem' }}>
            <FontAwesomeIcon icon="clock" className="mr-2" style={{ color: '#6c5ce7' }} />
            {title}
          </h2>
        </div>

        <table className="w-100" style={{ border: '1px solid #6c5ce7' }}>
          <thead style={{ backgroundColor: '#6c5ce7' }}>
            <tr className="text-white">
              {columns.length > 0 ? (
                columns.map((col, colIndex) => (
                  <th key={colIndex} style={{ fontWeight: 'normal', padding: '0.5em 1em' }}>
                    {col.displayHeader || col.sourceKey?.label || col.type?.label || 'Column'}
                  </th>
                ))
              ) : (
                <th style={{ fontWeight: 'normal', padding: '0.5em 1em' }}>
                  <em>No columns configured</em>
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            <tr style={{ color: 'gray', backgroundColor: '#f0effc', borderBottom: '1px solid #6c5ce7' }}>
              {columns.length > 0 ? (
                columns.map((col, colIndex) => (
                  <td key={colIndex} style={{ padding: '0.5em 1em' }}>
                    <em>{col.type?.label || 'data'}</em>
                  </td>
                ))
              ) : (
                <td style={{ padding: '0.5em 1em' }}>
                  <em>-</em>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const handleLayoutChange = (layout, area) => {
    setFieldValue(`layout.${area}`, layout);
  };

  return (<>
    {values.filters && values.filters.length > 0 && (
      <Card className="m-3">
        <CardBody className="p-2">
          <ReactGridLayout
            className="layout"
            rowHeight={60}
            cols={12}
            layout={values.layout.filters}
            onLayoutChange={(layout) => { handleLayoutChange(layout, 'filters'); }}
            resizeHandles={['se']}
            isBounded
            margin={[0, 0]}
          >
            {values.filters.map((f, i) => createFilterGridItem(f, i))}
          </ReactGridLayout>
        </CardBody>

        <CardFooter className="d-flex">
          <div className="ml-auto">
            <Button
              disabled
              className="rounded-0 p-1 bg-white text-primary"
              style={{
                width: '120px',
                height: '30px',
                border: '1px solid #D8D6D6',
                fontSize: '14px',
              }}
            >
              Export
            </Button>

            <Button
              disabled
              className="rounded-0 p-1 bg-primary text-white border-0"
              style={{
                width: '120px',
                height: '30px',
                fontSize: '14px',
              }}
            >
              Run Report
            </Button>
          </div>
        </CardFooter>
      </Card>
    )}

    <ReactGridLayout
      className="layout"
      rowHeight={60}
      cols={12}
      layout={values.layout.visualizations}
      onLayoutChange={(layout) => { handleLayoutChange(layout, 'visualizations'); }}
      resizeHandles={['se']}
      isBounded
    >
      {(values && values.charts ? values.charts : []).map((c, i) => createChartGridItem(c, i))}
      {(values && values.tables ? values.tables : []).map((t, i) => createTableGridItem(t, i))}
      {(values && values.lagTables ? values.lagTables : []).map((lt, i) => createLagTableGridItem(lt, i))}
    </ReactGridLayout>
  </>);
};

export default LayoutEditor;
